<script setup>
import { useDashToast as useToast } from '@dashhudson/dashing-ui/toast';
import { computed, ref, toRefs } from 'vue';

import CircularLoader from '@/components/CircularLoader.vue';
import Button from '@/components/foundation/Button.vue';
import Icon from '@/components/foundation/Icon.vue';
import AssistantMessageContent from '@/components/VisionAi/AssistantMessageContent.vue';
import AssistantStyleWrapper from '@/components/VisionAi/AssistantStyleWrapper.vue';
import {
  INTENT_TYPES,
  REGENERATE_TOOLTIPS,
  SUMMARY_INTENT_TYPES,
  USER_FEEDBACK,
  VISION_AI_SUMMARY_INTERACTIONS,
} from '@/components/VisionAi/constants';
import {
  ARTIFACT_MARKER_PATTERN,
  getPropsForIntentType,
  MESSAGE_WIDGET_MAP,
} from '@/components/VisionAi/utils/message';
import {
  aiMessageInteractionEvent,
  aiUserFeedbackEvent,
} from '@/components/VisionAi/utils/mixpanel-events';
import VisionToolbar from '@/components/VisionAi/VisionToolbar.vue';
import WidgetWrapper from '@/components/VisionAi/widgets/WidgetWrapper.vue';
import { toolTips } from '@/config';
import { useIdentityStore } from '@/stores/identity';
import { useVisionAiStore } from '@/stores/vision-ai';
import { useVisionAiMessagesStore } from '@/stores/vision-ai-messages';
import { colours, VISION_AI } from '@/ux/colours';

const props = defineProps({
  text: {
    type: String,
    default: '',
  },
  messageId: {
    type: Number,
    default: null,
  },
  time: {
    type: String,
    default: '',
  },
  date: {
    type: String,
    default: '',
  },
  sourceUrl: {
    type: [String, Object],
    default: null,
  },
  regeneratingMessageLoading: {
    type: Boolean,
    default: false,
  },
  disableRegenerateButton: {
    type: Boolean,
    default: null,
  },
  intentType: {
    type: String,
    default: '',
  },
  additionalContent: {
    type: Object,
    default: null,
  },
  intentQuery: {
    type: Object,
    default: null,
  },
  feedback: {
    type: String,
    default: null,
  },
});

const emit = defineEmits([
  'regenerateMessage',
  'copyResponse',
  'sourceButtonClicked',
  'expandMedia',
]);
const { sourceUrl, intentType } = toRefs(props);

const { addToast } = useToast({
  severity: 'info',
  message: `Thank you for the feedback`,
  life: 10000,
});

// stores
const identityStore = useIdentityStore();
const visionAiStore = useVisionAiStore();
const visionAiMessagesStore = useVisionAiMessagesStore();

const message = computed(() =>
  visionAiMessagesStore.messages.find((msg) => msg.id === props.messageId),
);

// consts
const thumbsUpIcon = ref(
  props.feedback === USER_FEEDBACK.POSITIVE ? 'thumbs-up-active' : 'thumbs-up-new',
);
const thumbsDownIcon = ref(
  props.feedback === USER_FEEDBACK.NEGATIVE ? 'thumbs-down-active' : 'thumbs-down-new',
);

// computed
const dateTooltipMessage = computed(() => `${props.date} at ${props.time}`);

const regenerateResponseTooltip = computed(() =>
  props.regeneratingMessageLoading || props.disableRegenerateButton
    ? REGENERATE_TOOLTIPS.LOADING
    : REGENERATE_TOOLTIPS.READY,
);

const isSamePageSource = computed(() => visionAiStore.checkIfSamePageSource(sourceUrl.value));

const isMetricsAnalytics = computed(() => props.intentType === INTENT_TYPES.METRIC_ANALYTICS);

const isSummaryIntent = computed(() => SUMMARY_INTENT_TYPES.includes(props.intentType));

const sourceTooltipMessage = computed(() =>
  isSamePageSource.value
    ? toolTips.visionAi.disabledSourceButton
    : toolTips.visionAi.activeSourceButton,
);

const showRegenerateButton = computed(() => {
  if (isSummaryIntent.value) return true;

  const mostRecentMessage =
    visionAiMessagesStore.messages?.[visionAiMessagesStore.messages.length - 1];
  return props.messageId === mostRecentMessage?.id;
});

function toggleThumbsUp() {
  const mostRecentMessageId =
    visionAiMessagesStore.messages?.[visionAiMessagesStore.messages.length - 1].id;
  if (thumbsUpIcon.value === 'thumbs-up-new') {
    thumbsUpIcon.value = 'thumbs-up-active';
    thumbsDownIcon.value = 'thumbs-down-new';
    addToast();
    visionAiMessagesStore.updateMessage({
      messageId: props.messageId,
      feedback: USER_FEEDBACK.POSITIVE,
    });
    aiUserFeedbackEvent({
      feedback: VISION_AI_SUMMARY_INTERACTIONS.THUMBS_UP,
      message: message.value,
    });
    return;
  }
  thumbsUpIcon.value = 'thumbs-up-new';
  visionAiMessagesStore.updateMessage({ messageId: mostRecentMessageId, feedback: null });
}

function toggleThumbsDown() {
  const mostRecentMessageId =
    visionAiMessagesStore.messages?.[visionAiMessagesStore.messages.length - 1].id;
  if (thumbsDownIcon.value === 'thumbs-down-new') {
    thumbsDownIcon.value = 'thumbs-down-active';
    thumbsUpIcon.value = 'thumbs-up-new';
    addToast();
    visionAiMessagesStore.updateMessage({
      messageId: props.messageId,
      feedback: USER_FEEDBACK.NEGATIVE,
    });
    aiUserFeedbackEvent({
      feedback: VISION_AI_SUMMARY_INTERACTIONS.THUMBS_DOWN,
      message: message.value,
    });
    return;
  }
  thumbsDownIcon.value = 'thumbs-down-new';
  visionAiMessagesStore.updateMessage({ messageId: mostRecentMessageId, feedback: null });
}

const assistantToolbarIcons = computed(() => {
  const icons = [
    {
      iconName: 'copy',
      iconColor: colours.TEXT.TEXT_LINK,
      tooltip: 'Copy response',
      showBorderOnHover: true,
      buttonClass: 'hover:bg-opacity-80',
      clickEvent: () => {
        aiMessageInteractionEvent({
          interaction: VISION_AI_SUMMARY_INTERACTIONS.COPY,
          intentType: props.intentType,
          messageId: props.messageId,
        });
        emit('copyResponse', props.text);
      },
      iconWidth: '18.67px',
      iconHeight: '18.67px',
    },
  ];
  if (showRegenerateButton.value) {
    icons.push({
      iconName: 'replay-2',
      iconColor: colours.TEXT.TEXT_LINK,
      iconHoverColor: colours.TEXT.TEXT_LINK,
      tooltip: regenerateResponseTooltip.value,
      showBorderOnHover: true,
      buttonClass: 'hover:!bg-opacity-80 !m-0',
      clickEvent: () => {
        aiMessageInteractionEvent({
          interaction: VISION_AI_SUMMARY_INTERACTIONS.REGENERATE,
          intentType: props.intentType,
          messageId: props.messageId,
        });
        emit('regenerateMessage', props.messageId);
      },
      iconWidth: '19.42px',
      iconHeight: '16.67px',
      iconLoading: props.regeneratingMessageLoading,
      disabled: props.disableRegenerateButton,
    });
  }
  icons.push({
    iconName: thumbsUpIcon.value,
    iconColor: colours.TEXT.TEXT_LINK,
    tooltip: REGENERATE_TOOLTIPS.THUMBS_UP,
    showBorderOnHover: true,
    buttonClass: 'hover:!bg-opacity-80 !m-0',
    clickEvent: () => {
      toggleThumbsUp();
    },
    iconWidth: '17px',
    iconHeight: '17px',
    iconLoading: props.regeneratingMessageLoading,
  });
  icons.push({
    iconName: thumbsDownIcon.value,
    iconColor: colours.TEXT.TEXT_LINK,
    tooltip: REGENERATE_TOOLTIPS.THUMBS_DOWN,
    showBorderOnHover: true,
    buttonClass: 'hover:!bg-opacity-80 !m-0',
    clickEvent: () => {
      toggleThumbsDown();
    },
    iconWidth: '17px',
    iconHeight: '17px',
    iconLoading: props.regeneratingMessageLoading,
  });
  return icons;
});

const widgetComponent = computed(() => MESSAGE_WIDGET_MAP[props.intentType]);

const widgetProps = computed(() => {
  const content = { ...props.additionalContent };

  if (props.intentType === INTENT_TYPES.COMMENTS) {
    const brandLabel = props.intentQuery?.sourceLinkParams?.brandLabel;
    const brandAvatarUrl = identityStore.identity.brands[brandLabel]?.avatarUrl;

    if (content?.media?.source && content.media.source.split('_')[0] === 'TWITTER') {
      content.brandAvatarUrl = content.media.userAvatar || brandAvatarUrl;
    } else {
      content.brandAvatarUrl = brandAvatarUrl;
    }
  }
  const additionalProps = {
    isExpanded: visionAiStore.popupIsExpanded,
    hasConversationSidebarExpanded: visionAiStore.showConversationHistorySidebar,
  };

  return getPropsForIntentType(props.intentType, content, props.intentQuery, additionalProps);
});

const uiWidgets = computed(() => {
  if (props.intentType === INTENT_TYPES.UNIFIED) {
    return widgetProps.value?.widgets ?? [];
  }
  return [];
});

// Extract artifact IDs referenced in the message text that match an actual widget,
// so we know which widgets to render inline vs. below the message.
const inlineWidgetIds = computed(() => {
  const artifactMatches = props.text.matchAll(new RegExp(ARTIFACT_MARKER_PATTERN, 'g'));
  const knownWidgetIds = new Set(uiWidgets.value.map((widget) => widget.id));
  return new Set(
    Array.from(artifactMatches, (match) => match[1]).filter((id) => knownWidgetIds.has(id)),
  );
});

function isMediaWidgetType(widgetType) {
  return widgetType === 'library_media' || widgetType === 'library_media_group';
}

// Returns widgets that should be rendered below the message text, excluding any
// that are already inlined via `[[ARTIFACT:id]]` placeholders. If any media widgets
// are inlined, all remaining media widgets are also excluded to avoid duplication.
const nonInlineWidgets = computed(() => {
  const hasInlineMediaWidgets = uiWidgets.value.some(
    (widget) => isMediaWidgetType(widget.type) && inlineWidgetIds.value.has(widget.id),
  );

  return uiWidgets.value.filter((widget) => {
    // If the widget is inlined, we don't want to render it below the message
    if (widget.id && inlineWidgetIds.value.has(widget.id)) {
      return false;
    }

    // If we've inlined any media widget types, we don't want to render any media widgets non-inline
    if (hasInlineMediaWidgets) {
      return !isMediaWidgetType(widget.type);
    }

    // Otherwise, we can render the widget below the message
    return true;
  });
});

const showExpandViewButton = computed(
  () => !visionAiStore.popupIsExpanded && visionAiStore.canAccessConversational,
);

const useLegacyHTML = computed(
  () =>
    // NOTE: Legacy responses always start with HTML tags, temp workaround
    props.text.startsWith('<p') || props.text.endsWith('/ul>'),
);

// methods
function handleSourceButtonClicked() {
  emit('sourceButtonClicked', intentType.value, sourceUrl.value);
  aiMessageInteractionEvent({
    interaction: VISION_AI_SUMMARY_INTERACTIONS.SOURCE_LINK,
    intentType: props.intentType,
    messageId: props.messageId,
  });
}

function handleDownloadButtonClicked() {
  aiMessageInteractionEvent({
    interaction: VISION_AI_SUMMARY_INTERACTIONS.DOWNLOAD_MEDIA,
    intentType: props.intentType,
    messageId: props.messageId,
  });
}

function handleMessageContentInteraction(interaction) {
  aiMessageInteractionEvent({
    interaction,
    intentType: props.intentType,
    messageId: props.messageId,
  });
}

defineExpose(
  process.env.NODE_ENV === 'test'
    ? {
        isSamePageSource,
        sourceTooltipMessage,
        isMetricsAnalytics,
      }
    : {},
);
</script>

<template>
  <AssistantStyleWrapper
    class="!w-full"
    :class="{ 'popup-expanded': visionAiStore.popupIsExpanded }"
    data-test="assistant-message"
  >
    <span v-if="useLegacyHTML" v-sanitize-html="props.text" class="sanitize-html-string" />
    <AssistantMessageContent
      v-else
      :text="props.text"
      :widgets="uiWidgets"
      :is-expanded="visionAiStore.popupIsExpanded"
      @message-interaction="handleMessageContentInteraction"
    />

    <VisionToolbar :clickable-icons="assistantToolbarIcons">
      <template
        #clickableIcons="{
          iconName,
          iconColor,
          iconHeight,
          iconWidth,
          iconHoverColor,
          iconLoading,
        }"
      >
        <CircularLoader v-if="iconLoading" />
        <Icon
          v-else
          :name="iconName"
          :color="iconColor"
          :hover-color="iconHoverColor"
          :height="iconHeight"
          :width="iconWidth"
        />
      </template>
    </VisionToolbar>

    <template v-if="props.additionalContent && props.intentType !== INTENT_TYPES.UNIFIED">
      <component
        :is="widgetComponent"
        v-bind="widgetProps"
        :vision-ai-report="true"
        @download-button-clicked="handleDownloadButtonClicked"
      />
    </template>

    <WidgetWrapper
      v-if="nonInlineWidgets.length > 0"
      :widgets="nonInlineWidgets"
      @on-widget-click="handleMessageContentInteraction"
    />

    <div class="!h-[1px] !w-full !bg-black !bg-opacity-[12%]" data-test="footer-divider"></div>

    <Button
      v-if="showExpandViewButton"
      link
      class="!cursor-pointer !text-[--text-link] !no-underline"
      data-test="expand-view-button"
      @click="emit('expandMedia')"
      >Expand to view more</Button
    >

    <VisionToolbar class="toolbar !w-full">
      <template #leftIcons>
        <div class="!flex !w-full !items-center !gap-2">
          <div v-tooltip="dateTooltipMessage" class="text-extra-small !cursor-default">
            {{ props.time }}
          </div>

          <div
            v-if="props.sourceUrl && !isMetricsAnalytics"
            v-tooltip="sourceTooltipMessage"
            class="!flex !items-center"
          >
            <Button
              id="source-url"
              :to="props.sourceUrl"
              class="!flex !items-center !no-underline"
              aria-label="View source for this summary"
              :style="{ color: VISION_AI.VISION_AI_03 }"
              :disabled="isSamePageSource"
              link
              :class="{ disabled: isSamePageSource }"
              @click="handleSourceButtonClicked"
            >
              <Icon name="link" xsmall :color="VISION_AI.VISION_AI_03" />
              <span class="text-extra-small-medium !ml-1">Source</span>
            </Button>
          </div>
        </div>
      </template>
    </VisionToolbar>
  </AssistantStyleWrapper>
</template>

<style lang="postcss" scoped>
.sanitize-html-string {
  :deep() {
    b,
    strong {
      font-weight: 600;
      font-size: inherit;
    }

    p:not(:last-child) {
      margin-bottom: var(--space-16);
    }

    i,
    em {
      font-style: italic;
      font-size: inherit;
    }

    ul {
      list-style-type: disc;
      padding-left: var(--space-20);
    }

    a {
      color: var(--text-link);
    }
  }
}

.assistant-bubble > .card:not([data-test^='Dash']) {
  max-width: 318px;
}

.toolbar {
  .disabled {
    pointer-events: none;
    cursor: default;
  }
}
</style>
